import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import SiteFooter from "@/components/site/SiteFooter";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-paper text-ink font-sans" data-testid="terms-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-14 lg:py-20">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-1.5 text-[12px] text-ink-soft hover:text-forest mb-6 transition-colors"
        >
          <ArrowLeft size={13} /> Volver al inicio
        </Link>
        <p className="text-[11px] tracking-[0.28em] uppercase text-copper font-medium">
          Legal · Versión 1.0 · Vigente desde el 1 de febrero de 2026
        </p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.05] text-balance">
          Términos del Servicio
        </h1>
        <p className="mt-4 text-[14px] text-ink-soft leading-relaxed">
          Estos Términos rigen el uso de la plataforma <strong>unaTESIS</strong>{" "}
          (en adelante, la <em>“Plataforma”</em>), operada por{" "}
          <strong>Marketing de Inteligencia Artificial S. de R.L. de C.V.</strong>{" "}
          (en adelante, <em>“unaTESIS”</em>, <em>“nosotros”</em> o el{" "}
          <em>“Proveedor”</em>), con domicilio en Av. Américas 1545, pisos 20,
          21, 22 y 23, Col. Providencia, Guadalajara, Jalisco, México;
          representada legalmente por el C. <strong>Ricardo Zermeño Mejía</strong>.
        </p>

        <article className="mt-10 flex flex-col gap-8 text-[14px] leading-relaxed text-ink">
          <Section n="1" title="Aceptación de los términos">
            Al registrarse, ingresar o utilizar la Plataforma, el usuario (en
            adelante, el <em>“Usuario”</em>) declara haber leído, comprendido y
            aceptado en su totalidad los presentes Términos del Servicio, así
            como el Aviso de Privacidad publicado en{" "}
            <Link to="/privacidad" className="text-forest underline">
              /privacidad
            </Link>
            . Si el Usuario no está de acuerdo con cualquier disposición, debe
            abstenerse de utilizar la Plataforma.
          </Section>

          <Section n="2" title="Descripción del servicio">
            unaTESIS es una plataforma SaaS de apoyo académico que ofrece, de
            manera enunciativa y no limitativa: editor de textos académicos con
            asistencia de inteligencia artificial, validador de coherencia
            metodológica, recuperación de fuentes científicas reales (RAG),
            métrica antiplagio, simulador de jurado, exportación a formatos
            DOCX bajo normativas universitarias y sellado criptográfico de
            autoría mediante huellas digitales SHA-256.
            <br /><br />
            <strong>El servicio es de naturaleza asistencial.</strong> La
            Plataforma no produce, presenta ni firma trabajos académicos en
            nombre del Usuario; tampoco sustituye el criterio académico,
            metodológico o ético del sustentante.
          </Section>

          <Section n="3" title="Cuenta y acceso">
            El Usuario debe ser mayor de edad o contar con el consentimiento
            del titular de la patria potestad o tutor legal. El Usuario es el
            único responsable de mantener la confidencialidad de sus
            credenciales y de toda actividad realizada bajo su cuenta. Debe
            notificar de inmediato cualquier acceso no autorizado al correo{" "}
            <a href="mailto:contacto@unatesis.com" className="text-forest underline">
              contacto@unatesis.com
            </a>.
          </Section>

          <Section n="4" title="Planes, precios y forma de pago">
            La Plataforma se ofrece bajo un modelo <em>freemium</em>: 180
            segundos de interacción activa sin costo y, posteriormente, un plan
            <strong> Premium </strong>de pago único de <strong>USD $69.00</strong>{" "}
            (sesenta y nueve dólares de los Estados Unidos de América) que
            otorga acceso de por vida para <strong>un (1) proyecto de tesis</strong>{" "}
            hasta su exportación final. El pago se procesa a través de Stripe.
            La Plataforma no almacena información financiera del Usuario.
          </Section>

          <Section n="5" title="Política de reembolso">
            El Usuario puede solicitar el reembolso íntegro del plan Premium
            dentro de los <strong>14 días naturales</strong> posteriores a la
            fecha del pago, siempre que no haya exportado el documento final.
            Solicitudes posteriores serán evaluadas caso por caso. Las
            solicitudes deben enviarse a{" "}
            <a href="mailto:contacto@unatesis.com" className="text-forest underline">
              contacto@unatesis.com
            </a>{" "}
            indicando el correo de la cuenta y el folio de pago.
          </Section>

          <Section n="6" title="Obligaciones del Usuario">
            El Usuario se obliga a: (i) utilizar la Plataforma exclusivamente
            con fines lícitos y académicos; (ii) garantizar que los datos,
            fuentes y resultados que cargue al sistema sean producto de su
            propia investigación; (iii) <strong>no</strong> presentar como
            propias obras enteramente generadas por la inteligencia artificial,
            entendiendo que el servicio ofrece asistencia metodológica y de
            redacción, no autoría; (iv) respetar la propiedad intelectual y la
            normativa académica de su institución; y (v) no intentar vulnerar,
            descompilar o realizar ingeniería inversa de la Plataforma.
          </Section>

          <Section n="7" title="Propiedad intelectual del Usuario">
            El Usuario conserva <strong>la titularidad íntegra</strong> de las
            obras y contenidos que produzca utilizando la Plataforma. unaTESIS
            no reclama derecho patrimonial alguno sobre la tesis o documentos
            generados por el Usuario. La huella criptográfica registrada en el
            sello SHA-256 sirve únicamente como prueba de existencia y
            anterioridad, sin transferir derechos al Proveedor.
          </Section>

          <Section n="8" title="Propiedad intelectual de la Plataforma">
            Los nombres, logotipos, código fuente, diseño, modelos, prompts
            internos y demás elementos que conforman la Plataforma son
            propiedad exclusiva de Marketing de Inteligencia Artificial S. de
            R.L. de C.V., y están protegidos por la Ley Federal del Derecho de
            Autor y los tratados internacionales aplicables.
          </Section>

          <Section n="9" title="Servicios de terceros">
            La Plataforma utiliza servicios de terceros para operar, incluidos
            de manera enunciativa: Stripe (procesamiento de pagos), Anthropic
            (modelos de lenguaje Claude), OpenAI (modelos de lenguaje GPT),
            OpenAlex y Semantic Scholar (catálogos académicos abiertos), y
            proveedores de infraestructura en la nube. El Usuario acepta que
            sus datos podrán ser tratados por dichos terceros únicamente en la
            medida necesaria para proveer el servicio y conforme al Aviso de
            Privacidad.
          </Section>

          <Section n="10" title="Disponibilidad y limitación de responsabilidad">
            unaTESIS realiza esfuerzos razonables para mantener el servicio
            disponible 24/7, pero <strong>no garantiza disponibilidad
            ininterrumpida ni libre de errores</strong>. El Proveedor no será
            responsable por: (i) decisiones académicas tomadas por el Usuario
            con base en las sugerencias generadas; (ii) calificaciones,
            aprobaciones o rechazos por parte de instituciones educativas;
            (iii) pérdida de datos derivada de causas ajenas al control
            razonable del Proveedor; ni (iv) daños indirectos, lucro cesante o
            consecuenciales. La responsabilidad máxima agregada de unaTESIS
            frente al Usuario, por cualquier causa, no excederá el monto
            efectivamente pagado por el Usuario en los doce (12) meses previos
            al evento que origine la reclamación.
          </Section>

          <Section n="11" title="Deslinde sobre el resultado académico">
            El Usuario reconoce y acepta expresamente que unaTESIS es una
            <strong> herramienta de apoyo metodológico, redacción y formato</strong>,
            y que la presentación, defensa, aprobación, calificación,
            modificación, cancelación, rechazo, suspensión, anulación o
            cualquier otro resultado relacionado con su trabajo de tesis,
            proyecto recepcional o documento académico es{" "}
            <strong>responsabilidad única y exclusiva del Usuario</strong> y
            de la institución educativa correspondiente.
            <br /><br />
            De manera enunciativa y no limitativa, unaTESIS <strong>NO se hace
            responsable</strong> por: (i) la no aprobación, reprobación o
            calificación obtenida; (ii) observaciones, correcciones o
            comentarios emitidos por asesores, sinodales, jurados, revisores o
            comités académicos; (iii) la cancelación, anulación o invalidación
            de la tesis por parte de la universidad, escuela o autoridad
            académica, sea cual fuere su causa, incluyendo cuestiones de
            originalidad, metodología, formato, fondo, forma o cualquier otra;
            (iv) cambios en los criterios, reglamentos o políticas de
            titulación de la institución educativa; (v) decisiones del Usuario
            de adoptar, modificar o descartar las sugerencias generadas por la
            inteligencia artificial; (vi) la pérdida de cupos, plazos, fechas
            de titulación, becas, distinciones o cualquier oportunidad
            académica o profesional; ni (vii) sanciones académicas o
            disciplinarias derivadas del uso que el Usuario dé al contenido
            generado.
            <br /><br />
            El Usuario manifiesta entender que el éxito de su tesis depende de
            su propio criterio académico, dedicación, investigación original y
            cumplimiento de los lineamientos de su institución. La sola
            utilización de la Plataforma no garantiza, ni explícita ni
            implícitamente, la aprobación del trabajo académico.
          </Section>

          <Section n="12" title="Suspensión y terminación">
            unaTESIS podrá suspender o dar por terminada la cuenta del
            Usuario, sin reembolso, en caso de uso indebido, fraude académico
            comprobado, intento de manipulación del sistema, o por solicitud
            de autoridad competente. El Usuario podrá cerrar su cuenta en
            cualquier momento escribiendo a{" "}
            <a href="mailto:contacto@unatesis.com" className="text-forest underline">
              contacto@unatesis.com
            </a>.
          </Section>

          <Section n="13" title="Modificaciones">
            unaTESIS podrá modificar estos Términos en cualquier momento.
            Toda modificación entrará en vigor a partir de su publicación en la
            Plataforma. Los cambios sustanciales serán notificados al correo
            registrado del Usuario con al menos quince (15) días naturales de
            anticipación. El uso continuado de la Plataforma posterior a dicho
            plazo se considerará aceptación de los nuevos Términos.
          </Section>

          <Section n="14" title="Jurisdicción y ley aplicable">
            Estos Términos se rigen por las leyes federales de los Estados
            Unidos Mexicanos. Para la interpretación, ejecución y cumplimiento
            del presente instrumento, las partes se someten expresamente a la
            jurisdicción de los tribunales competentes de la ciudad de
            Guadalajara, Jalisco, renunciando a cualquier otro fuero que
            pudiera corresponderles por razón de sus domicilios presentes o
            futuros.
          </Section>

          <Section n="15" title="Contacto">
            Para cualquier duda, queja o aclaración respecto a estos Términos,
            escriba a{" "}
            <a href="mailto:contacto@unatesis.com" className="text-forest underline">
              contacto@unatesis.com
            </a>
            , dirigido al representante legal C. Ricardo Zermeño Mejía.
          </Section>
        </article>

        <p className="mt-12 text-[12px] text-ink-soft italic">
          Última actualización: 1 de febrero de 2026.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`terms-section-${n}`}>
      <h2 className="font-serif text-2xl text-ink mb-2 leading-snug">
        <span className="text-copper font-medium">{n}.</span> {title}
      </h2>
      <div className="text-ink-soft">{children}</div>
    </section>
  );
}
